import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from './../../../../environments/environment';
import { LoggerService } from './../../../services/logger.service';

/**
 * GitLab Options
 * --------------
 * Sub-formulario montado dinámicamente por el ExportPanel cuando el usuario
 * elige GitLab como destino. Recoge:
 *   - Project destino (select cargado de /api/integrations/gitlab/projects).
 *   - Prefix opcional para el title del issue.
 *
 * GitLab no tiene "issue types" (decisión #1 del Punto 9), así que el form
 * es más simple que el de Jira. El handler aplica labels semánticas
 * automáticamente.
 *
 * Estilo: usa las clases globales `.integration-options`, `.integration-field`,
 * `.field-select`, etc. definidas en `styles.scss` sección 3b (refactor DRY).
 *
 * Contrato con el panel (idéntico al de JiraOptions):
 *   @Output optionsChanged   - emite las opciones cada vez que cambian.
 *   @Output validityChanged  - emite true/false; el panel habilita EXPORTAR.
 *   reload()                 - re-cargable tras conectar GitLab.
 *
 * @author Marcos Cordo
 * @version 11/06/2026_1
 */

export interface GitlabProjectDto {
  id: number;
  name: string;
  nameWithNamespace: string;
  path: string;
  pathWithNamespace: string;
  webUrl: string;
  avatarUrl?: string | null;
}

export interface GitlabExporterOptions {
  projectId: number;
  projectName: string;
  summaryPrefix?: string;
}

@Component({
  selector: 'app-gitlab-options',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gitlab-options.component.html',
  styleUrls: ['./gitlab-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GitlabOptionsComponent implements OnInit {
  /** Emite las opciones actuales cada vez que el usuario las modifica. */
  @Output() optionsChanged = new EventEmitter<GitlabExporterOptions>();
  /** Emite true cuando las opciones son válidas para enviar el job. */
  @Output() validityChanged = new EventEmitter<boolean>();

  loading = signal(true);
  errorMessage = signal<string | null>(null);
  projects = signal<GitlabProjectDto[]>([]);

  selectedProjectId: number | null = null;
  summaryPrefix = '';

  private http = inject(HttpClient);
  private logger = inject(LoggerService);

  async ngOnInit(): Promise<void> {
    await this.reload();
  }

  /**
   * Carga proyectos del backend. Idempotente — re-invocable cuando el chip
   * de conexión emite (connectionChanged).
   */
  async reload(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const resp = await firstValueFrom(
        this.http.get<GitlabProjectDto[]>(`${environment.api_service_integrations}/gitlab/projects`)
      );
      const list = Array.isArray(resp) ? resp : [];
      this.projects.set(list);

      // Auto-selección: primer proyecto.
      this.selectedProjectId = list.length > 0 ? list[0].id : null;
      this.emit();
    } catch (err: any) {
      const status = err?.status;
      // 412 = backend dice "sin tokens GitLab/Jira" (precondición no cumplida).
      // 401 se mantiene por compat defensiva (cualquier cliente antiguo o caso
      // legacy donde el backend devuelva 401 por sesión expirada de verdad
      // ya habrá sido capturado por el interceptor global antes de llegar aquí).
      if (status === 412 || status === 401) {
        this.errorMessage.set('Sin conexión activa con GitLab. Usa el botón "CONECTAR GITLAB".');
      } else if (status === 403) {
        this.errorMessage.set('Sin permisos para acceder a GitLab.');
      } else {
        this.logger.error('[GitlabOptions] No se pudieron cargar proyectos:', err);
        this.errorMessage.set('No se pudieron cargar los proyectos.');
      }
      this.projects.set([]);
      this.emit();
    } finally {
      this.loading.set(false);
    }
  }

  get selectedProject(): GitlabProjectDto | null {
    if (this.selectedProjectId === null) return null;
    return this.projects().find(p => p.id === this.selectedProjectId) || null;
  }

  onProjectChange(): void { this.emit(); }
  onPrefixChange(): void  { this.emit(); }

  private emit(): void {
    const proj = this.selectedProject;
    const valid = !!proj;
    if (valid && proj) {
      this.optionsChanged.emit({
        projectId: proj.id,
        projectName: proj.nameWithNamespace || proj.name,
        summaryPrefix: this.summaryPrefix?.trim() || undefined
      });
    }
    this.validityChanged.emit(valid);
  }
}